import { group } from '@angular/animations';
import { HttpClient } from '@angular/common/http';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { first } from 'rxjs';
import { GetAllCountriesResponse } from 'src/app/models/get-all-countries-response.model';
import { GetRegionCountriesResponse } from 'src/app/models/get-region-countries-response.model';
import {
  MapConfig,
  MapData,
} from 'src/app/models/mecs-models/mecs-map-data.model';
import { TooltipState } from 'src/app/models/mecs-models/mecs-tooltop-state.model';
import { CountryService } from 'src/app/services/country.service';
import { ColorService } from 'src/app/services/mecs-services/color.service';

@Component({
  selector: 'app-mecs-map',
  templateUrl: './mecs-map.component.html',
  styleUrls: ['./mecs-map.component.scss'],
})
export class MecsMapComponent {
  @Input() config!: MapConfig;
  @Output() clickedRegion: EventEmitter<any> = new EventEmitter<any>();

  regions: GetRegionCountriesResponse[] = [];

  private countryData: MapData[] = [];
  allCountries: GetAllCountriesResponse[] = [];
  countryLabels: SVGTextElement[] = [];
  markers: SVGElement[] = [];
  tooltips: TooltipState[] = [];
  svgMapLoaded = false;

  private zoom: any = {
    scale: 1,
    translateX: 0,
    translateY: 0,
  };

  constructor(
    private renderer: Renderer2,
    private elRef: ElementRef,
    private http: HttpClient,
    private colorService: ColorService,
    private countryService: CountryService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config']) {
      this.updateMapData();

      if (!this.svgMapLoaded) {
        this.loadSvgMap();
      }
    }
  }

  updateMapData(): void {
    if (this.config?.data) {
      this.countryData = this.config.data;
    }
  }

  loadSvgMap(): void {
    this.countryService
      .getAllCountries()
      .pipe(first())
      .subscribe((response) => {
        this.allCountries = response;
        this.http
          .get('assets/images/world-map.min.svg', { responseType: 'text' })
          .pipe(first())
          .subscribe((svgData) => {
            const mapContainer =
              this.elRef.nativeElement.querySelector('.map-container');
            const parser = new DOMParser();
            const svgDoc = parser.parseFromString(svgData, 'image/svg+xml')
              .documentElement as unknown as SVGElement;

            this.setupSvgAttributes(svgDoc);
            this.applyRandomColors(svgDoc);
            this.renderer.appendChild(mapContainer, svgDoc);

            const markerGroup = this.renderer.createElement('g', 'svg');
            this.renderer.setAttribute(markerGroup, 'class', 'markers');
            this.renderer.appendChild(svgDoc, markerGroup);

            this.setupHoverEffect(svgDoc);

            this.svgMapLoaded = true;
          });

        this.countryService
          .getRegionCountries()
          .pipe(first())
          .subscribe((regionResponse) => {
            this.regions = regionResponse;
          });
      });
  }

  private setupSvgAttributes(svgDoc: SVGElement): void {
    this.renderer.removeAttribute(svgDoc, 'width');
    this.renderer.removeAttribute(svgDoc, 'height');

    this.renderer.setAttribute(
      svgDoc,
      'viewBox',
      '30.767 241.591 784.077 458.627'
    );
    this.renderer.setAttribute(svgDoc, 'preserveAspectRatio', 'xMidYMid meet');

    this.zoom = {
      scale: 1,
      translateX: 0,
      translateY: 0,
    };

    this.updateSvgTransform(svgDoc);
  }

  private applyRandomColors(svgDoc: SVGElement): void {
    // First: handle individual paths with IDs
    const paths = svgDoc.querySelectorAll('path[id]');
    paths.forEach((path) => {
      const countryCode = path.id.toLowerCase();
      const country = this.allCountries.find(
        (c) => c.countryCode.toLowerCase() === countryCode
      );

      let fillColor = '#D7D5D6';
      if (country && country.isActive) {
        fillColor = this.colorService.getRandomColor();
      }

      this.renderer.setAttribute(path, 'fill', fillColor);
      this.renderer.setAttribute(path, 'data-original-color', fillColor);

      this.renderer.listen(path, 'mouseenter', () => {
        this.renderer.setAttribute(path, 'fill', 'gray');
      });

      this.renderer.listen(path, 'mouseleave', () => {
        const originalColor =
          path.getAttribute('data-original-color') || '#D7D5D6';
        this.renderer.setAttribute(path, 'fill', originalColor);
      });
    });

    // Then: handle <g> groups with IDs
    const groups = svgDoc.querySelectorAll('g[id]');
    groups.forEach((group) => {
      const countryCode = group.id.toLowerCase();
      const country = this.allCountries.find(
        (c) => c.countryCode.toLowerCase() === countryCode
      );

      let fillColor = '#D7D5D6';
      if (country && country.isActive) {
        fillColor = this.colorService.getRandomColor();
      }

      // Apply color to each path within the group
      const groupPaths = group.querySelectorAll('path');
      groupPaths.forEach((path) => {
        this.renderer.setAttribute(path, 'fill', fillColor);
        this.renderer.setAttribute(path, 'data-original-color', fillColor);

        this.renderer.listen(path, 'mouseenter', () => {
          this.renderer.setAttribute(path, 'fill', 'gray');
        });

        this.renderer.listen(path, 'mouseleave', () => {
          const originalColor =
            path.getAttribute('data-original-color') || '#D7D5D6';
          this.renderer.setAttribute(path, 'fill', originalColor);
        });
      });
    });
  }

  private setupHoverEffect(svgDoc: SVGElement): void {
    // Select all <path> and <g> elements (individual countries and regions)
    const paths = svgDoc.querySelectorAll('path, g');

    paths.forEach((pathOrGroup) => {
      if (pathOrGroup.tagName.toLowerCase() === 'g') {
        // Region grouping, add listeners to children
        const groupPaths = pathOrGroup.querySelectorAll('path');
        const groupId = pathOrGroup.id?.toUpperCase();

        if (groupId) {
          const originalColor = groupPaths[0].getAttribute('fill') || '#D7D5D6';
          groupPaths.forEach((path) => {
            path.setAttribute('data-original-color', originalColor);

            this.renderer.listen(path, 'mouseenter', () => {
              const region = this.getRegionByCountryCode(groupId);
              if (region) {
                this.colorRegion(region, svgDoc);
              }

              groupPaths.forEach((p) => {
                this.renderer.setAttribute(p, 'fill', 'gray');
              });
            });

            this.renderer.listen(path, 'mouseleave', () => {
              groupPaths.forEach((p) => {
                const originalColor =
                  p.getAttribute('data-original-color') || '#D7D5D6';
                this.renderer.setAttribute(p, 'fill', originalColor);
              });

              const region = this.getRegionByCountryCode(groupId);
              if (region) {
                this.restoreRegionColor(region, svgDoc);
              }
            });

            this.renderer.listen(path, 'click', () => {
              this.onRegionClick(groupId);
            });
          });
        }
      } else {
        const originalColor = pathOrGroup.getAttribute('fill') || '#D7D5D6';
        pathOrGroup.setAttribute('data-original-color', originalColor);

        this.renderer.listen(pathOrGroup, 'mouseenter', () => {
          const countryCode = pathOrGroup.id?.toUpperCase();
          const region = this.getRegionByCountryCode(countryCode);
          if (region) {
            this.colorRegion(region, svgDoc);
          }

          this.renderer.setAttribute(pathOrGroup, 'fill', 'gray');
        });

        this.renderer.listen(pathOrGroup, 'mouseleave', () => {
          const originalColor =
            pathOrGroup.getAttribute('data-original-color') || '#D7D5D6';
          this.renderer.setAttribute(pathOrGroup, 'fill', originalColor);

          const countryCode = pathOrGroup.id?.toUpperCase();
          const region = this.getRegionByCountryCode(countryCode);
          if (region) {
            this.restoreRegionColor(region, svgDoc);
          }
        });

        this.renderer.listen(pathOrGroup, 'click', () => {
          this.onRegionClick(pathOrGroup.id);
        });
      }
    });
  }

  onRegionClick(countryId: string): void {
    if (countryId !== '') {
      const upperCountryId = countryId.toUpperCase();

      const region = this.regions.find((r) =>
        r.countries.some((c) => c.countryCode.toUpperCase() === upperCountryId)
      );

      if (region) {
        this.clickedRegion.emit(region.id);
      }
    }
  }

  private colorRegion(regionName: string, svgDoc: SVGElement): void {
    const region = this.regions.find((r) => r.region === regionName);
    if (region) {
      region.countries.forEach((country) => {
        const countryPath = svgDoc.querySelector(
          `#${country.countryCode.toLowerCase()}`
        );

        if (countryPath?.tagName.toLowerCase() === 'g') {
          const groupPaths = countryPath.querySelectorAll('path');
          groupPaths.forEach((p) => {
            this.renderer.setAttribute(p, 'fill', 'gray');
          });
        } else if (countryPath) {
          this.renderer.setAttribute(countryPath, 'fill', 'gray');
        }
      });
    }
  }

  private restoreRegionColor(regionName: string, svgDoc: SVGElement): void {
    const region = this.regions.find((r) => r.region === regionName);
    if (region) {
      region.countries.forEach((country) => {
        const countryPath = svgDoc.querySelector(
          `#${country.countryCode.toLowerCase()}`
        );

        if (countryPath?.tagName.toLowerCase() === 'g') {
          const groupPaths = countryPath.querySelectorAll('path');
          groupPaths.forEach((p) => {
            const originalColor = p.getAttribute('data-original-color');
            this.renderer.setAttribute(p, 'fill', originalColor || '#D7D5D6');
          });
        } else if (countryPath) {
          const originalColor = countryPath.getAttribute('data-original-color');
          this.renderer.setAttribute(
            countryPath,
            'fill',
            originalColor || '#D7D5D6'
          );
        }
      });
    }
  }

  private getRegionByCountryCode(countryCode: string): string | null {
    for (const region of this.regions) {
      const country = region.countries.find(
        (c) => c.countryCode.toUpperCase() === countryCode
      );
      if (country) {
        return region.region;
      }
    }
    return null;
  }

  private updateSvgTransform(svgDoc: SVGElement): void {
    const transform = `translate(${this.zoom.translateX}px, ${this.zoom.translateY}px) scale(${this.zoom.scale})`;
    this.renderer.setStyle(svgDoc, 'transform', transform);
  }
}
